import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Loader2, RefreshCcwDot, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { PublicLayout } from '@/components/public/PublicLayout';
import { publicGet, publicPost } from '@/lib/api/publicClient';
import { recurringPortalEndpoints } from '@/lib/api/endpoints';
import { formatDateOnly } from '@/lib/dates/date-only';

interface RenewalAuthorizationContext {
  jobId: string;
  jobName: string;
  jobNumber: string | null;
  companyName: string | null;
  timezone: string | null;
  startDate: string | null;
  endDate: string | null;
  baseAmount: number | null;
  billingMode: string | null;
  lineItems: Array<{ name: string; quantity: number | null }>;
  termMonths: number | null;
  recurrence: { freq: string; interval: number } | null;
  previousRate: number | null;
  paymentMethod: { brand: string | null; last4: string | null; type: string } | null;
  alreadyAuthorized: boolean;
  canAuthorize: boolean;
}

const BILLING_MODE_LABEL: Record<string, string> = {
  PER_VISIT: 'Billed per visit',
  SUBSCRIPTION: 'Billed monthly',
  PREPAID: 'Billed once per term',
};

function frequencyLabel(recurrence: { freq: string; interval: number } | null): string | null {
  if (!recurrence) return null;
  const unit: Record<string, [string, string]> = {
    DAILY: ['day', 'days'],
    WEEKLY: ['week', 'weeks'],
    MONTHLY: ['month', 'months'],
    YEARLY: ['year', 'years'],
  };
  const pair = unit[recurrence.freq];
  if (!pair) return null;
  return recurrence.interval > 1
    ? `Service every ${recurrence.interval} ${pair[1]}`
    : `Service every ${pair[0]}`;
}

// §8.5 — two customer flows off the renewal emails, both action-on-click (not
// page load) so email link prefetch can't mutate anything:
// - `?jobId=`      → one-click auto-renewal opt-out (renewal notice email).
// - `?authorize=`  → renewal re-authorization: the original billing consent
//   lapsed, the successor is parked in DRAFT, and this captures a fresh
//   consent record so service + automatic billing continue.
export const Route = createFileRoute('/(public)/recurring-renewal/$token')({
  validateSearch: (search: Record<string, unknown>): { jobId?: string; authorize?: string } => ({
    jobId: typeof search.jobId === 'string' ? search.jobId : undefined,
    authorize: typeof search.authorize === 'string' ? search.authorize : undefined,
  }),
  component: RecurringRenewalPage,
});

function RecurringRenewalPage() {
  const { authorize } = Route.useSearch();
  return authorize ? <RenewalAuthorizePage successorJobId={authorize} /> : <RenewalOptOutPage />;
}

function formatMoney(amount: number) {
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function RenewalAuthorizePage({ successorJobId }: { successorJobId: string }) {
  const { token } = Route.useParams();
  const [signedName, setSignedName] = useState('');

  const contextQuery = useQuery<RenewalAuthorizationContext>({
    queryKey: ['public', 'renewal-authorization', token, successorJobId],
    queryFn: () => publicGet(recurringPortalEndpoints.renewalAuthorization(token, successorJobId)),
    retry: false,
  });

  const authorizeMutation = useMutation({
    mutationFn: () =>
      publicPost(recurringPortalEndpoints.renewalAuthorization(token, successorJobId), {
        signedName: signedName.trim(),
      }),
  });

  const context = contextQuery.data;
  const done = authorizeMutation.isSuccess || context?.alreadyAuthorized;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-md px-4 py-10">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="size-5" />
              Authorize your renewal
            </CardTitle>
            <CardDescription>
              {context?.companyName
                ? `${context.companyName} needs your authorization to continue automatic billing for the renewed term.`
                : 'Your authorization is needed to continue automatic billing for the renewed term.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {contextQuery.isLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            ) : contextQuery.isError || !context ? (
              <p className="text-sm text-destructive">
                This authorization link is invalid or has expired. Please contact your service
                provider.
              </p>
            ) : (
              <>
                <div className="space-y-1.5 rounded-lg border bg-muted/20 p-4 text-sm">
                  <p className="font-medium text-foreground">
                    {context.jobNumber
                      ? `${context.jobNumber} · ${context.jobName}`
                      : context.jobName}
                  </p>
                  {context.startDate ? (
                    <p className="text-muted-foreground">
                      Renewed term starts {formatDateOnly(context.startDate)}
                      {context.endDate
                        ? ` and runs through ${formatDateOnly(context.endDate)}`
                        : ''}
                      {context.termMonths ? ` (${context.termMonths}-month term)` : ''}.
                    </p>
                  ) : null}
                  {frequencyLabel(context.recurrence) || context.billingMode ? (
                    <p className="text-muted-foreground">
                      {[
                        frequencyLabel(context.recurrence),
                        context.billingMode ? BILLING_MODE_LABEL[context.billingMode] : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  ) : null}
                  {context.baseAmount != null ? (
                    <p className="text-muted-foreground">
                      Rate:{' '}
                      {context.previousRate != null &&
                      context.previousRate > 0 &&
                      context.previousRate !== context.baseAmount ? (
                        <>
                          <span className="line-through">{formatMoney(context.previousRate)}</span>{' '}
                          <span className="font-medium text-foreground">
                            {formatMoney(context.baseAmount)}
                          </span>
                          <span>
                            {' '}
                            ({context.baseAmount > context.previousRate ? '+' : ''}
                            {(
                              ((context.baseAmount - context.previousRate) / context.previousRate) *
                              100
                            ).toFixed(1)}
                            % per your agreement)
                          </span>
                        </>
                      ) : (
                        <span className="font-medium text-foreground">
                          {formatMoney(context.baseAmount)}
                        </span>
                      )}
                    </p>
                  ) : null}
                  {context.paymentMethod ? (
                    <p className="text-muted-foreground">
                      Payment method:{' '}
                      <span className="font-medium text-foreground">
                        {context.paymentMethod.brand ?? context.paymentMethod.type}
                        {context.paymentMethod.last4 ? ` •••• ${context.paymentMethod.last4}` : ''}
                      </span>
                    </p>
                  ) : null}
                  {context.lineItems.length > 0 ? (
                    <div className="mt-2 border-t pt-2">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                        What&apos;s included
                      </p>
                      <ul className="mt-1 space-y-0.5 text-muted-foreground">
                        {context.lineItems.slice(0, 6).map((item, i) => (
                          <li key={`${item.name}-${i}`}>
                            {item.quantity != null && item.quantity !== 1
                              ? `${item.quantity}× `
                              : ''}
                            {item.name}
                          </li>
                        ))}
                        {context.lineItems.length > 6 ? (
                          <li>+ {context.lineItems.length - 6} more</li>
                        ) : null}
                      </ul>
                    </div>
                  ) : null}
                </div>

                {done ? (
                  <p className="flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle2 className="size-4" />
                    Renewal authorized — automatic billing will continue. Nothing else to do.
                  </p>
                ) : !context.canAuthorize ? (
                  <p className="text-sm text-muted-foreground">
                    This renewal no longer needs authorization.
                  </p>
                ) : (
                  <>
                    {authorizeMutation.isError ? (
                      <p className="text-sm text-destructive">
                        We couldn&apos;t record your authorization right now. Please try again or
                        contact your service provider.
                      </p>
                    ) : null}
                    <p className="text-xs text-muted-foreground">
                      By signing below, you agree that the payment method shown above may be charged
                      automatically for the renewed term, under the same terms you originally
                      accepted (price adjusted per your agreement).
                    </p>
                    <div className="space-y-1.5">
                      <Label htmlFor="signed-name">Type your full name to sign</Label>
                      <Input
                        id="signed-name"
                        value={signedName}
                        onChange={(event) => setSignedName(event.target.value)}
                        placeholder="Full name"
                        maxLength={120}
                        autoComplete="name"
                      />
                    </div>
                    <Button
                      className="w-full"
                      disabled={authorizeMutation.isPending || !signedName.trim()}
                      onClick={() => authorizeMutation.mutate()}
                    >
                      {authorizeMutation.isPending ? (
                        <>
                          <Loader2 className="mr-1.5 size-4 animate-spin" />
                          Authorizing…
                        </>
                      ) : (
                        'Sign & authorize automatic billing'
                      )}
                    </Button>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}

function RenewalOptOutPage() {
  const { token } = Route.useParams();
  const { jobId } = Route.useSearch();

  const optOut = useMutation({
    mutationFn: () =>
      publicPost(recurringPortalEndpoints.setAutoRenew(token, jobId ?? ''), {
        autoRenew: false,
      }),
  });

  return (
    <PublicLayout>
      <div className="mx-auto max-w-md px-4 py-10">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCcwDot className="size-5" />
              Turn off auto-renewal
            </CardTitle>
            <CardDescription>
              Your current term will finish as scheduled and the recurring service won&apos;t roll
              into a new term.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!jobId ? (
              <p className="text-sm text-destructive">
                This opt-out link is missing its service reference. Please use the link from your
                renewal email.
              </p>
            ) : optOut.isSuccess ? (
              <p className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 className="size-4" />
                Auto-renewal is now off. Nothing else to do.
              </p>
            ) : (
              <>
                {optOut.isError ? (
                  <p className="text-sm text-destructive">
                    We couldn&apos;t update this right now. The link may have expired — please
                    contact your service provider.
                  </p>
                ) : null}
                <Button
                  className="w-full"
                  disabled={optOut.isPending}
                  onClick={() => optOut.mutate()}
                >
                  {optOut.isPending ? (
                    <>
                      <Loader2 className="mr-1.5 size-4 animate-spin" />
                      Updating…
                    </>
                  ) : (
                    'Turn off auto-renewal'
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
